import * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shape variant of the skeleton */
  variant?: 'text' | 'circular' | 'rectangular';
  /** Animation type */
  animation?: 'pulse' | 'wave' | 'shimmer' | 'none';
  /** Convenience prop for width (can be number for px or string for any unit) */
  width?: string | number;
  /** Convenience prop for height (can be number for px or string for any unit) */
  height?: string | number;
}

const animationClasses = {
  pulse: 'animate-pulse',
  wave: 'animate-skeleton-wave',
  shimmer: 'animate-skeleton-shimmer',
  none: '',
} as const;

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    { className, variant = 'rectangular', animation = 'pulse', width, height, style, ...props },
    ref,
  ) => {
    const sizeStyle = {
      ...style,
      ...(width !== undefined && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(height !== undefined && { height: typeof height === 'number' ? `${height}px` : height }),
    };

    return (
      <div
        ref={ref}
        className={cn(
          'bg-muted rounded',
          animationClasses[animation],
          variant === 'text' && 'h-4 w-full',
          variant === 'circular' && 'h-10 w-10 rounded-full',
          variant === 'rectangular' && 'h-20 w-full',
          className,
        )}
        style={sizeStyle}
        {...props}
      />
    );
  },
);
Skeleton.displayName = 'Skeleton';

export { Skeleton };
